import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Mode } from '@web/contexts/appModeContext'
import { Animated, NativeScrollEvent, NativeSyntheticEvent, View } from 'react-native'
import { useModalize } from 'react-native-modalize'

import { isWeb } from '@common/config/env'
import useDebounce from '@common/hooks/useDebounce'
import useTheme from '@common/hooks/useTheme'
import PendingActionWindowModal from '@common/modules/dashboard/components/PendingActionWindowModal'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import GasTankModal from '@web/components/GasTankModal'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import { getUiType } from '@web/utils/uiType'
import { FEATURES } from '@web/constants/features'

import DAppFooter from '../components/DAppFooter'
import DashboardOverview from '../components/DashboardOverview'
import DashboardPages from '../components/DashboardPages'
import getStyles from './styles'

import Header from '@web/components/Header'
import Text from '@common/components/Text'
import PureSpinner from '@common/assets/svg/PureSpinner'
import useBackgroundService from '@web/hooks/useBackgroundService'
import { isExtension } from '@web/constants/browserapi'
import { EXTENSION_PRIVATE_CHAIN_IDS } from '@web/constants/extensionChains'
import useAppMode from '@web/hooks/useAppMode'
import { ModeEnum } from '@web/contexts/appModeContext'
import useExtensionAwareNetworks from '@web/hooks/useExtensionAwareNetworks'
import useRailgunContext from '@web/hooks/useRailgunContext'
import useRailgunBatchUpdate from '@web/hooks/useRailgunBatchUpdate'
import { getBalanceUsdByChain } from '@web/utils/railgunTokens'
import useKeystoreControllerState from '@web/hooks/useKeystoreControllerState'
import { computePrivateModeReason, isPrivateModeBlocked } from '@web/utils/privateModeAvailability'
import {
  resolveDashboardNetworkFilterForModeChange,
  resolveExtensionPrivateInvariantFilter
} from '@common/modules/dashboard/utils/dashboardModeNetworkFilter'

const { isPopup } = getUiType()

export const OVERVIEW_CONTENT_MAX_HEIGHT = 120

const DashboardScreen = () => {
  const { mode, setMode } = useAppMode()
  const { styles, theme } = useTheme(getStyles)
  const { ref: gasTankModalRef, open: openGasTankModal, close: closeGasTankModal } = useModalize()
  const lastOffsetY = useRef(0)
  const scrollUpStartedAt = useRef(0)
  const [dashboardOverviewSize, setDashboardOverviewSize] = useState({
    width: 0,
    height: 0
  })
  const debouncedDashboardOverviewSize = useDebounce({ value: dashboardOverviewSize, delay: 100 })
  const animatedOverviewHeight = useRef(new Animated.Value(OVERVIEW_CONTENT_MAX_HEIGHT)).current

  const { account, portfolio, dashboardNetworkFilter } = useSelectedAccountControllerState()
  const { dispatch } = useBackgroundService()
  const { networks } = useExtensionAwareNetworks()
  const { currentRailgunWallet, allRailgunWallets, isLoading: railgunIsLoading } = useRailgunContext()
  const {
    railgunEngineInitializing,
    railgunEngineInitializingError,
    ensureRailgunEngine
  } = useAppMode()
  const { keys } = useKeystoreControllerState()
  const batch = useRailgunBatchUpdate()

  const hasRailgunWallet = useMemo(() => {
    return allRailgunWallets.some((wallet) => wallet.accountAddr === account?.addr)
  }, [allRailgunWallets, account?.addr])

  const privateModeReason = useMemo(() => {
    const base = computePrivateModeReason({ account, keys, hasRailgunWallet })
    // Reflect ephemeral engine states for accounts that *would* be available once setup
    // finishes. Only override `creatable` — never replace 'no-seed' / 'hardware' / 'view-only',
    // which are structural and don't depend on engine state.
    if (base !== 'creatable') return base
    if (railgunEngineInitializingError) return 'failed'
    if (railgunEngineInitializing) return 'initializing'
    return base
  }, [
    account,
    keys,
    hasRailgunWallet,
    railgunEngineInitializing,
    railgunEngineInitializingError
  ])

  const firstPrivateSupportedChainId = useMemo(() => {
    const set = new Set(EXTENSION_PRIVATE_CHAIN_IDS.map((id) => id.toString()))
    const networkChainIdToExistsMap: Record<string, true> = {}
    networks.forEach((n) => {
      networkChainIdToExistsMap[n.chainId.toString()] = true
    })

    const balanceTokenMap = currentRailgunWallet?.balanceTokenMap
    const prices = currentRailgunWallet?.balanceTokenPriceMap
    const railgunUsdByChain =
      balanceTokenMap && prices ? getBalanceUsdByChain(balanceTokenMap, prices) : undefined

    const sortedPrivateChainIds = networks
      .map((n) => n.chainId.toString())
      .filter((chainId) => set.has(chainId) && networkChainIdToExistsMap[chainId])
      .sort((a, b) => {
        const aUsd = railgunUsdByChain?.get(Number(a)) ?? 0
        const bUsd = railgunUsdByChain?.get(Number(b)) ?? 0
        return bUsd - aUsd
      })

    if (sortedPrivateChainIds.length) return sortedPrivateChainIds[0]
    return EXTENSION_PRIVATE_CHAIN_IDS[0]?.toString()
  }, [currentRailgunWallet?.balanceTokenMap, currentRailgunWallet?.balanceTokenPriceMap, networks])

  const handleModeChange = useCallback(
    (nextMode: Mode) => {
      const nextFilter = resolveDashboardNetworkFilterForModeChange({
        previousMode: mode,
        nextMode,
        currentFilter: dashboardNetworkFilter,
        isExtension,
        firstPrivateSupportedChainId
      })
      if (nextFilter !== undefined) {
        dispatch({
          type: 'SELECTED_ACCOUNT_SET_DASHBOARD_NETWORK_FILTER',
          params: { dashboardNetworkFilter: nextFilter }
        })
      }
      if (nextMode === ModeEnum.PRIVATE) {
        // Wait for the dashboard network filter to be set before switching to private mode.
        setTimeout(() => {
          setMode(nextMode)
        }, 50)
      } else {
        setMode(nextMode)
      }
    },
    [mode, dashboardNetworkFilter, dispatch, firstPrivateSupportedChainId, setMode]
  )

  // If the user is in private mode and doesn't have a railgun wallet (could be hardware wallet), switch to public mode.
  // Gate on `railgunIsLoading=false` so transient empty states during the railgun wallet
  // load loop (or controller-state replacements on mobile-web) don't bounce the user
  // back to PUBLIC mid-session.
  // Also bounce a view-only / hardware / raw-private-key account out of private
  // mode even when a stale railgun wallet row exists for its address (so
  // hasRailgunWallet alone wouldn't trigger the bounce).
  useEffect(() => {
    if (mode !== ModeEnum.PRIVATE) return
    if (railgunIsLoading) return
    if (hasRailgunWallet && !isPrivateModeBlocked(privateModeReason)) return
    handleModeChange(ModeEnum.PUBLIC)
  }, [hasRailgunWallet, privateModeReason, mode, handleModeChange, railgunIsLoading])

  // When opening the dashboard in private mode (via URL or async dispatch), ensure the network filter is set to a valid chain.
  useEffect(() => {
    if (!isExtension || mode !== ModeEnum.PRIVATE || !firstPrivateSupportedChainId) return

    const next = resolveExtensionPrivateInvariantFilter(
      dashboardNetworkFilter,
      firstPrivateSupportedChainId
    )
    if (next !== undefined) {
      dispatch({
        type: 'SELECTED_ACCOUNT_SET_DASHBOARD_NETWORK_FILTER',
        params: { dashboardNetworkFilter: next }
      })
    }
  }, [mode, dashboardNetworkFilter, dispatch, firstPrivateSupportedChainId])

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!isPopup) return

      const {
        contentOffset: { y }
      } = event.nativeEvent

      if (scrollUpStartedAt.current === 0 && lastOffsetY.current > y) {
        scrollUpStartedAt.current = y
      } else if (scrollUpStartedAt.current > 0 && y > lastOffsetY.current) {
        scrollUpStartedAt.current = 0
      }
      lastOffsetY.current = y

      // The user has to scroll down the height of the overview container in order make it smaller.
      // This is done, because hiding the overview will subtract the height of the overview from the height of the
      // scroll view, thus a shorter scroll container may no longer be scrollable after hiding the overview
      // and if that happens, the user will not be able to scroll up to expand the overview again.
      const scrollDownThreshold = dashboardOverviewSize.height
      // scrollUpThreshold must be a constant value and not dependent on the height of the overview,
      // because the height will change as the overview animates from small to large.
      const scrollUpThreshold = 200
      const isOverviewExpanded =
        y < scrollDownThreshold || y < scrollUpStartedAt.current - scrollUpThreshold

      Animated.spring(animatedOverviewHeight, {
        toValue: isOverviewExpanded ? OVERVIEW_CONTENT_MAX_HEIGHT : 0,
        bounciness: 0,
        speed: 2.8,
        overshootClamping: true,
        // Drives layout props (paddingTop/paddingBottom/maxHeight) which the native
        // animated module does not support — must run on JS driver on all platforms.
        useNativeDriver: false
      }).start()
    },
    [animatedOverviewHeight, dashboardOverviewSize.height, lastOffsetY, scrollUpStartedAt]
  )

  return (
    <>
      {FEATURES.gasTankUiEnabled && (
        <GasTankModal
          modalRef={gasTankModalRef}
          handleClose={closeGasTankModal}
          portfolio={portfolio}
          account={account}
        />
      )}

      <PendingActionWindowModal />
      <Header
        mode={mode}
        onModeChange={handleModeChange}
        privateModeReason={privateModeReason}
        onRetryRailgunInit={ensureRailgunEngine}
      />
      <View style={styles.container}>
        <View style={[flexbox.flex1, spacings.ptSm]}>
          <DashboardOverview
            mode={mode}
            openGasTankModal={FEATURES.gasTankUiEnabled ? openGasTankModal : () => {}}
            animatedOverviewHeight={animatedOverviewHeight}
            dashboardOverviewSize={debouncedDashboardOverviewSize}
            setDashboardOverviewSize={setDashboardOverviewSize}
            privateModeReason={privateModeReason}
          />
          {mode === ModeEnum.PRIVATE && batch && (
            <View
              style={[
                flexbox.directionRow,
                flexbox.alignCenter,
                flexbox.justifyCenter,
                spacings.mvTy,
                {
                  gap: 8
                }
              ]}
            >
              <PureSpinner style={{ width: 20, height: 20 }} color={theme.primary} />
              <Text fontSize={12} weight="semiBold">
                {batch.status}
              </Text>
            </View>
          )}
          <DashboardPages onScroll={onScroll} animatedOverviewHeight={animatedOverviewHeight} />
        </View>
        <DAppFooter />
      </View>
    </>
  )
}

export default React.memo(DashboardScreen)
